import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { documentService } from '../services/api';
import { HostelDocument } from '../types';
import { creatorDisplayName } from '../utils/creatorDisplay';
import './DocumentManagement.css';

function formatFileSize(bytes?: number): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(val?: string): string {
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function fileTypeLabel(mime?: string, fileName?: string): string {
  if (mime) {
    if (mime.startsWith('image/')) return 'Image';
    if (mime === 'application/pdf') return 'PDF';
    if (mime.includes('word') || mime.includes('document')) return 'Document';
    if (mime.includes('sheet') || mime.includes('excel')) return 'Spreadsheet';
    if (mime.startsWith('video/')) return 'Video';
    if (mime.startsWith('text/')) return 'Text';
  }
  const ext = (fileName || '').split('.').pop()?.toLowerCase();
  if (!ext) return 'File';
  return ext.toUpperCase();
}

function canPreviewInBrowser(mime?: string): boolean {
  if (!mime) return false;
  return mime.startsWith('image/') || mime === 'application/pdf' || mime.startsWith('text/');
}

const DocumentManagement: React.FC = () => {
  const { hostel } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [documents, setDocuments] = useState<HostelDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [previewDoc, setPreviewDoc] = useState<HostelDocument | null>(null);

  const loadDocuments = useCallback(async () => {
    if (!hostel?.id) {
      setDocuments([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await documentService.getDocumentsByHostel(hostel.id);
      setDocuments(data);
    } catch {
      toast.error('Failed to load documents');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [hostel?.id]);

  useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return documents;
    return documents.filter((d) => {
      const hay = [d.title, d.description, d.fileName, d.mimeType, creatorDisplayName(d)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [documents, search]);

  const resetUploadForm = () => {
    setTitle('');
    setDescription('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const closeUpload = () => {
    if (submitting) return;
    setShowUpload(false);
    resetUploadForm();
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hostel?.id) return;
    if (!title.trim()) {
      toast.error('Title is required.');
      return;
    }
    if (!file) {
      toast.error('Please choose a file to upload.');
      return;
    }
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('hostelId', String(hostel.id));
      fd.append('title', title.trim());
      if (description.trim()) fd.append('description', description.trim());
      fd.append('file', file);
      await documentService.uploadDocument(fd);
      toast.success('Document uploaded');
      closeUpload();
      await loadDocuments();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Upload failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleView = (doc: HostelDocument) => {
    if (canPreviewInBrowser(doc.mimeType)) {
      setPreviewDoc(doc);
      return;
    }
    window.open(doc.fileUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = (doc: HostelDocument) => {
    const a = document.createElement('a');
    a.href = doc.fileUrl;
    a.download = doc.fileName || doc.title || 'document';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDelete = async (doc: HostelDocument) => {
    const ok = window.confirm(`Delete "${doc.title}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await documentService.deleteDocument(doc.id);
      toast.success('Document deleted');
      if (previewDoc?.id === doc.id) setPreviewDoc(null);
      await loadDocuments();
    } catch {
      toast.error('Delete failed');
    }
  };

  if (!hostel) {
    return <div className="docs-loading">Loading…</div>;
  }

  return (
    <div className="docs-page">
      <header className="docs-header">
        <div>
          <h1 className="docs-title">Documents</h1>
        </div>
        <button type="button" className="docs-btn docs-btn--primary" onClick={() => setShowUpload(true)}>
          + Upload document
        </button>
      </header>

      <section className="docs-card">
        <div className="docs-toolbar">
          <label className="docs-search">
            <span aria-hidden>🔍</span>
            <input
              type="search"
              placeholder="Search by title, file name, type…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <span className="docs-count">{filtered.length} document{filtered.length === 1 ? '' : 's'}</span>
        </div>

        {loading ? (
          <p className="docs-empty">Loading documents…</p>
        ) : filtered.length === 0 ? (
          <p className="docs-empty">
            {documents.length === 0
              ? 'No documents yet. Upload your first file.'
              : 'No documents match your search.'}
          </p>
        ) : (
          <div className="docs-table-wrap responsive-table-wrap">
            <table className="docs-table responsive-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>File</th>
                  <th>Type</th>
                  <th>Size</th>
                  <th>Uploaded by</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((doc) => (
                  <tr key={doc.id}>
                    <td className="responsive-table__primary" data-label="Title">
                      <span className="docs-cell-title">{doc.title}</span>
                      {doc.description ? (
                        <span className="docs-cell-desc">{doc.description}</span>
                      ) : null}
                    </td>
                    <td className="docs-cell-file responsive-table__full" data-label="File">{doc.fileName || '—'}</td>
                    <td data-label="Type">
                      <span className="docs-type-badge">{fileTypeLabel(doc.mimeType, doc.fileName)}</span>
                    </td>
                    <td data-label="Size">{formatFileSize(doc.fileSize)}</td>
                    <td data-label="Uploaded by">{creatorDisplayName(doc)}</td>
                    <td data-label="Date">{formatDate(doc.createdAt)}</td>
                    <td className="responsive-table__actions" data-label="Actions">
                      <div className="docs-actions">
                        <button type="button" className="docs-action docs-action--view" onClick={() => handleView(doc)}>
                          View
                        </button>
                        <button
                          type="button"
                          className="docs-action docs-action--download"
                          onClick={() => handleDownload(doc)}
                        >
                          Download
                        </button>
                        <button
                          type="button"
                          className="docs-action docs-action--delete"
                          onClick={() => void handleDelete(doc)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showUpload && (
        <div className="docs-modal-overlay" role="presentation" onClick={closeUpload}>
          <div className="docs-modal" role="dialog" aria-labelledby="docs-upload-title" onClick={(e) => e.stopPropagation()}>
            <div className="docs-modal-head">
              <h2 id="docs-upload-title">Upload document</h2>
              <button type="button" className="docs-modal-close" onClick={closeUpload} disabled={submitting} aria-label="Close">
                ×
              </button>
            </div>
            <form className="docs-form" onSubmit={(e) => void handleUpload(e)}>
              <label className="docs-field">
                <span>
                  Title <span className="docs-req">*</span>
                </span>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Fire safety certificate"
                  required
                  disabled={submitting}
                />
              </label>
              <label className="docs-field">
                <span>Description</span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional notes"
                  rows={3}
                  disabled={submitting}
                />
              </label>
              <label className="docs-field">
                <span>
                  File <span className="docs-req">*</span>
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="*/*"
                  disabled={submitting}
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                {file ? <span className="docs-file-hint">{file.name} · {formatFileSize(file.size)}</span> : null}
                <span className="docs-file-note">Any file type · max 25 MB</span>
              </label>
              <div className="docs-form-actions">
                <button type="button" className="docs-btn" onClick={closeUpload} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="docs-btn docs-btn--primary" disabled={submitting}>
                  {submitting ? 'Uploading…' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {previewDoc && (
        <div className="docs-modal-overlay docs-preview-overlay" role="presentation" onClick={() => setPreviewDoc(null)}>
          <div className="docs-preview-modal" role="dialog" aria-label="Document preview" onClick={(e) => e.stopPropagation()}>
            <div className="docs-modal-head">
              <h2>{previewDoc.title}</h2>
              <button type="button" className="docs-modal-close" onClick={() => setPreviewDoc(null)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="docs-preview-body">
              {previewDoc.mimeType?.startsWith('image/') ? (
                <img src={previewDoc.fileUrl} alt={previewDoc.title} className="docs-preview-image" />
              ) : previewDoc.mimeType === 'application/pdf' ? (
                <iframe title={previewDoc.title} src={previewDoc.fileUrl} className="docs-preview-frame" />
              ) : (
                <iframe title={previewDoc.title} src={previewDoc.fileUrl} className="docs-preview-frame" />
              )}
            </div>
            <div className="docs-preview-actions">
              <button type="button" className="docs-btn" onClick={() => handleDownload(previewDoc)}>
                Download
              </button>
              <button type="button" className="docs-btn docs-btn--primary" onClick={() => window.open(previewDoc.fileUrl, '_blank')}>
                Open in new tab
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentManagement;
